import type { SpeakerCard, SpeakerFieldErrors } from './speakers.types';

import {
  Accordion,
  IconButton,
  InputDescription,
  PlusIcon,
  TextInput,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { SpeakerImageUploader } from './speaker-image-uploader';
import { MAX_SPEAKER_NAME_LENGTH, MAX_SPEAKER_POSITION_LENGTH } from './speakers.validation';

import styles from './speaker-profile-card.module.css';

type Props = {
  index: number;
  card: SpeakerCard;
  errors?: SpeakerFieldErrors;
  unsavedChanges?: boolean;
  uploading?: boolean;
  disabled?: boolean;
  deleteDisabled?: boolean;
  onImageSelected: (cardKey: string, file: File) => void;
  onImageRemoved: (cardKey: string) => void;
  onNameChange: (cardKey: string, value: string) => void;
  onNameBlur: (cardKey: string) => void;
  onPositionChange: (cardKey: string, value: string) => void;
  onPositionBlur: (cardKey: string) => void;
  onDelete: (cardKey: string) => void;
};

export function SpeakerProfileCard({
  index,
  card,
  errors,
  unsavedChanges,
  uploading,
  disabled,
  deleteDisabled,
  onImageSelected,
  onImageRemoved,
  onNameChange,
  onNameBlur,
  onPositionChange,
  onPositionBlur,
  onDelete,
}: Props) {
  const { t } = useTranslation();

  const testId = `speaker-${index}`;
  // Prefer the locally-picked preview so the new (not-yet-uploaded) image is what's shown.
  const imageUrl = card.previewUrl ?? card.picture?.downloadLink;
  const label = card.name.trim() || t('manager.webcastSetup.speakers.card.defaultLabel');

  return (
    <div className={styles.cardWrapper}>
      <Accordion
        data-testid={testId}
        buttonId={`${testId}-accordion-button`}
        buttonClassName={styles.accordionButton}
        compact
        // Always open: the header stays a static label (+ unsaved hint + delete); the body never collapses.
        isOpen
        onToggle={() => undefined}
        buttonContent={
          <div className={styles.headerContent}>
            <PlusIcon />
            <span className={styles.label}>{label}</span>
            {unsavedChanges && (
              <span className={styles.unsavedChanges} data-testid={`${testId}-unsaved-hint`}>
                {t('manager.webcastSetup.eventDescription.unsavedChanges')}
              </span>
            )}
          </div>
        }
      >
        <div className={styles.content}>
          <SpeakerImageUploader
            testIdPrefix={`${testId}-image`}
            name={card.name}
            imageUrl={imageUrl}
            uploading={uploading}
            disabled={disabled}
            onFileSelected={(file) => onImageSelected(card.cardKey, file)}
            onRemove={() => onImageRemoved(card.cardKey)}
          />

          <div className={styles.fields}>
            <InputDescription
              inputId={`${testId}-name`}
              label={t('manager.webcastSetup.speakers.name.label')}
              disabled={disabled}
              errorMessage={errors?.name}
            >
              <TextInput
                data-testid={`${testId}-name`}
                maxLength={MAX_SPEAKER_NAME_LENGTH}
                inputSize="small"
                disabled={disabled}
                value={card.name}
                onChange={(event) => onNameChange(card.cardKey, event.target.value)}
                onBlur={() => onNameBlur(card.cardKey)}
              />
            </InputDescription>

            <InputDescription
              inputId={`${testId}-position`}
              label={t('manager.webcastSetup.speakers.position.label')}
              disabled={disabled}
              errorMessage={errors?.position}
            >
              <TextInput
                data-testid={`${testId}-position`}
                maxLength={MAX_SPEAKER_POSITION_LENGTH}
                inputSize="small"
                disabled={disabled}
                value={card.position}
                onChange={(event) => onPositionChange(card.cardKey, event.target.value)}
                onBlur={() => onPositionBlur(card.cardKey)}
              />
            </InputDescription>
          </div>
        </div>
      </Accordion>

      {/* Delete lives in the header (Figma): an absolutely-positioned sibling overlay so it isn't a
          button nested inside the accordion's header button. The Tooltip wrapper is `display:
          contents`, so the positioning has to live on this wrapping div. */}
      <div className={styles.deleteButton}>
        <Tooltip label={t('manager.webcastSetup.speakers.delete.disabledWhileLive')} hidden={!deleteDisabled}>
          <IconButton
            data-testid={`${testId}-delete-button`}
            rounded
            small
            type="button"
            disabled={disabled || deleteDisabled}
            aria-label={t('manager.webcastSetup.speakers.delete.button')}
            onClick={() => onDelete(card.cardKey)}
          >
            <TrashIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
